import { PlusOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Empty,
  Flex,
  FormInstance,
  InputNumber,
  Select,
  Spin,
  Table
} from 'antd'
import { FormListFieldData, TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useStocksServiceGetMedicineListEndpointKey } from 'src/api/queries'
import {
  GetMedicineListResponse,
  InternalInventoryDocumentResponse2,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  EditableFormItem,
  requiredRules,
  SubmitButtons,
  Text
} from 'src/components'
import { useStyles } from 'src/features/stock/components/inputs/styles'
import { useDebounce } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  item?: InternalInventoryDocumentResponse2
  fields: FormListFieldData[]
  form?: FormInstance
  add: () => void
  remove: (index: number) => void
}

const fieldsToReset = [
  'productId',
  'barCode',
  'name',
  'producerName',
  'expirationDate',
  'quantity'
]

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      fieldsToReset.map((field) => ['medicine', index, field])
    )
    .flat()

export const LocalInputsConsumablesTable = ({
  fields,
  item,
  form,
  add,
  remove
}: Props) => {
  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [search, setSearch] = useState('')

  const palette = useTheme()

  const debouncedSearch = useDebounce(search)
  const { styles } = useStyles()
  const { t } = useLocales()

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetMedicineListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getMedicineListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const medicines = medicinesData?.pages.flatMap((page) => page.data) || []

  const options = medicines.map((item) => ({
    value: item?.id,
    label: `${item?.commercialName} (${t('stock_applications.series')}: ${item?.code} - ${t('common.producer')}: ${item?.producer?.name} )`,
    item
  }))

  const addConsumable = async () => {
    const medicines = form?.getFieldValue('medicine')

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewConsumable(true)
  }

  const onSelect = (index: number, value: string) => {
    const data = form?.getFieldValue('medicine')

    const selectedItem = options?.find((item) => item?.value === value)

    if (selectedItem) {
      data[index] = {
        ...data[index],
        productId: selectedItem?.value,
        name: selectedItem?.item?.internationalName?.name,
        barCode: selectedItem?.item?.code,
        producerName: selectedItem?.item?.producer?.name,
        expirationDate: selectedItem?.item?.registrationDate
          ? dayjs(selectedItem?.item?.registrationDate)
              .add(selectedItem?.item?.validityPeriod ?? 0, 'month')
              .format(yearMonthDaySlash)
          : undefined
      }

      setSearch('')
      form?.setFieldValue('medicine', data)
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue('medicine')

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({ medicine: values?.medicine })
    }
  }

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(fieldsToReset.map((field) => ['stock', index, field]))
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const columns: TableColumnsType = [
    {
      key: 'barCode',
      width: '10%',
      title: t('common.code'),
      render: (_, __, index) => {
        const code = form?.getFieldValue(['medicine', index, 'barCode'])

        return code ?? '-'
      }
    },
    {
      key: 'name',
      width: '35%',
      title: t('common.commercial_naming'),
      render: (_, __, index) => {
        return item ? (
          <Text weight={400} color={palette.blue6}>
            {form?.getFieldValue(['medicine', index, 'name'])}
          </Text>
        ) : (
          <EditableFormItem
            name={[index, 'name']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Select
              allowClear
              showSearch
              popupMatchSelectWidth={false}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
              onClear={() => setSearch('')}
              loading={isFetchingMedicines}
              options={options}
              onSelect={(value) => onSelect(index, value)}
              placeholder={t('common.active_component')}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  hasNextMedicinesPage && fetchNextMedicinesPage()
              }}
              notFoundContent={
                isFetchingMedicines ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'producerName',
      width: '15%',
      title: t('common.producer'),
      render: (_, __, index) => {
        return (
          <Text
            weight={400}
            color={palette.cyan7}
            ellipsis
            className={styles.producerText}
          >
            {form?.getFieldValue(['medicine', index, 'producerName'])}
          </Text>
        )
      }
    },
    {
      key: 'expirationDate',
      width: '10%',
      title: t('common.expiration_date'),
      render: (_, __, index) => {
        const expirationDate = form?.getFieldValue([
          'medicine',
          index,
          'expirationDate'
        ])

        return expirationDate
          ? dayjs(expirationDate).format(dayMonthYearBackSlash)
          : '-'
      }
    },
    {
      key: 'quantity',
      width: '15%',
      title: t('common.quantity'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'quantity']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <InputNumber
              min={0}
              placeholder={t('common.quantity')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },

    ...(!item
      ? [
          {
            width: 105,
            key: 'actions',
            fixed: 'right' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => remove(index)}
              />
            )
          }
        ]
      : [])
  ]

  return (
    <Table
      className={styles.table}
      id="scrollableTable"
      dataSource={fields}
      bordered
      pagination={false}
      scroll={{ x: 'max-content' }}
      columns={columns}
      footer={() =>
        !item && (
          <Flex justify="center">
            <Button onClick={addConsumable} icon={<PlusOutlined />}>
              {t('analysis.add_test.add_consumable')}
            </Button>
          </Flex>
        )
      }
    />
  )
}
